/**
 * What-Works Signal Engine — derive per-author performance signals from the
 * post performance ledger, with SAMPLE-SIZE HONESTY baked in.
 *
 * The Typefully/Claude-ritual insight: generic best practices are table
 * stakes — the moat is knowing what works for THIS author. Given logged
 * posts (from `postPerformance.ts`), this module groups them along five
 * dimensions (format, hookStyle, topic, length, postTime), compares each
 * group's mean engagement to the author's overall mean (`lift`), and grades
 * how much the evidence should be trusted:
 *
 *   n < minWeakN   → strength 'none'  — NO signal is claimed from n=2
 *   n < minStrongN → strength 'weak'  — suggestive, shown with its n
 *   otherwise      → strength 'strong'
 *
 * Every signal carries its `evidencePostIds` so the UI can render evidence
 * cards, not vibes. `diffSignalSets` powers the "question-hooks upgraded
 * weak→strong (n 8→14)" moment, and `coldStartWeight` makes the personal-
 * signal weight transparent while the ledger is still thin.
 *
 * PURE by contract: in-memory records in, plain data out. No store, no
 * firebase, no network, no randomness.
 */

import { engagementRate, type LoggedPost } from './postPerformance';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The dimensions signals are derived along. */
export type SignalDimension = 'format' | 'hookStyle' | 'topic' | 'length' | 'postTime';

/** One derived per-author signal with its evidence. */
export type Signal = {
  dimension: SignalDimension;
  /** The grouped value, e.g. 'question', 'linkedinPost', 'short', 'morning'. */
  value: string;
  /** Group mean engagement / overall mean engagement. 1 = exactly average. */
  lift: number;
  /** How many logged posts back this signal — the honest n. */
  sampleSize: number;
  /** Sample-size-honest evidence grade. 'none' means "do not claim this yet". */
  strength: 'none' | 'weak' | 'strong';
  /** The exact posts backing the signal, for evidence cards. */
  evidencePostIds: string[];
};

/** A full derivation run over the ledger. */
export type SignalSet = {
  signals: Signal[];
  totalPosts: number;
  computedAt: number;
};

/** Default thresholds — exported so the honesty policy is auditable. */
export const DEFAULT_MIN_WEAK_N = 3;
export const DEFAULT_MIN_STRONG_N = 8;

/** Posts to log before personal signals reach full weight. */
export const COLD_START_FULL_WEIGHT_POSTS = 20;

// ---------------------------------------------------------------------------
// Bucketing
// ---------------------------------------------------------------------------

/** Word-count → human length bucket. */
export function lengthBucket(wordCount: number): string {
  if (wordCount < 50) return 'short';
  if (wordCount <= 150) return 'medium';
  return 'long';
}

/** Posted hour (0–23) → human time-of-day bucket. */
export function postTimeBucket(hour: number): string {
  if (hour >= 0 && hour <= 5) return 'overnight';
  if (hour <= 11) return 'morning';
  if (hour <= 17) return 'afternoon';
  return 'evening';
}

/** The grouped value of a post along one dimension. */
function valueOf(post: LoggedPost, dimension: SignalDimension): string {
  switch (dimension) {
    case 'format':
      return post.format;
    case 'hookStyle':
      return post.hookStyle;
    case 'topic':
      return post.topic;
    case 'length':
      return lengthBucket(post.wordCount);
    case 'postTime':
      return postTimeBucket(post.postedAtHour);
  }
}

const DIMENSIONS: readonly SignalDimension[] = [
  'format',
  'hookStyle',
  'topic',
  'length',
  'postTime',
];

const STRENGTH_RANK: Record<Signal['strength'], number> = {
  none: 0,
  weak: 1,
  strong: 2,
};

const round2 = (n: number) => Math.round(n * 100) / 100;

// ---------------------------------------------------------------------------
// Derivation
// ---------------------------------------------------------------------------

/**
 * Derive the full per-author signal set from logged posts. For every
 * dimension, posts are grouped by value; each group's mean engagement rate
 * is compared to the OVERALL mean to compute `lift`; and strength is
 * assigned with sample-size honesty (see module doc). Deterministic apart
 * from `computedAt`. An empty ledger honestly yields zero signals.
 */
export function deriveSignals(
  posts: LoggedPost[],
  opts?: { minStrongN?: number; minWeakN?: number },
): SignalSet {
  const minWeakN = opts?.minWeakN ?? DEFAULT_MIN_WEAK_N;
  const minStrongN = opts?.minStrongN ?? DEFAULT_MIN_STRONG_N;
  const computedAt = Date.now();

  if (posts.length === 0) {
    return { signals: [], totalPosts: 0, computedAt };
  }

  const overallMean =
    posts.reduce((sum, p) => sum + engagementRate(p.metrics), 0) / posts.length;

  const signals: Signal[] = [];
  for (const dimension of DIMENSIONS) {
    const groups = new Map<string, LoggedPost[]>();
    for (const post of posts) {
      const value = valueOf(post, dimension);
      const group = groups.get(value);
      if (group) group.push(post);
      else groups.set(value, [post]);
    }
    for (const [value, group] of groups) {
      const groupMean =
        group.reduce((sum, p) => sum + engagementRate(p.metrics), 0) / group.length;
      // When the author has NO measurable engagement anywhere, every group is
      // exactly average — lift 1, never a divide-by-zero fabrication.
      const lift = overallMean > 0 ? round2(groupMean / overallMean) : 1;
      const n = group.length;
      const strength: Signal['strength'] =
        n < minWeakN ? 'none' : n < minStrongN ? 'weak' : 'strong';
      signals.push({
        dimension,
        value,
        lift,
        sampleSize: n,
        strength,
        evidencePostIds: group.map((p) => p.id),
      });
    }
  }

  // Strongest evidence first, then biggest lift; stable dimension order last.
  signals.sort((a, b) => {
    const rank = STRENGTH_RANK[b.strength] - STRENGTH_RANK[a.strength];
    if (rank !== 0) return rank;
    if (b.lift !== a.lift) return b.lift - a.lift;
    const dim = DIMENSIONS.indexOf(a.dimension) - DIMENSIONS.indexOf(b.dimension);
    if (dim !== 0) return dim;
    return a.value < b.value ? -1 : a.value > b.value ? 1 : 0;
  });

  return { signals, totalPosts: posts.length, computedAt };
}

// ---------------------------------------------------------------------------
// Diffing — the "question-hooks upgraded weak→strong" moment
// ---------------------------------------------------------------------------

/**
 * Compare two signal sets. `upgraded` / `downgraded` are signals present in
 * BOTH sets (same dimension + value) whose strength rose / fell; `added` are
 * signals that only exist in `next`. Returned signals are always the NEXT
 * set's version (current n, current lift).
 */
export function diffSignalSets(
  prev: SignalSet,
  next: SignalSet,
): { upgraded: Signal[]; downgraded: Signal[]; added: Signal[] } {
  const key = (s: Signal) => `${s.dimension}\u0000${s.value}`;
  const prevByKey = new Map(prev.signals.map((s) => [key(s), s]));

  const upgraded: Signal[] = [];
  const downgraded: Signal[] = [];
  const added: Signal[] = [];

  for (const signal of next.signals) {
    const before = prevByKey.get(key(signal));
    if (!before) {
      added.push(signal);
      continue;
    }
    const delta = STRENGTH_RANK[signal.strength] - STRENGTH_RANK[before.strength];
    if (delta > 0) upgraded.push(signal);
    else if (delta < 0) downgraded.push(signal);
  }

  return { upgraded, downgraded, added };
}

// ---------------------------------------------------------------------------
// Cold start
// ---------------------------------------------------------------------------

/**
 * How much weight personal signals deserve given how many posts back them:
 * 0 at zero posts, ramping linearly to 1 at
 * {@link COLD_START_FULL_WEIGHT_POSTS}. Transparent by design — the UI shows
 * this number so the operator knows how personalized the advice really is.
 */
export function coldStartWeight(totalPosts: number): number {
  if (!Number.isFinite(totalPosts) || totalPosts <= 0) return 0;
  return Math.min(1, totalPosts / COLD_START_FULL_WEIGHT_POSTS);
}
